/**
 * PiataProducatori — marketplace public pentru micii producători.
 * Listare 100% gratuită · comision 1% per tranzacție · plată card (Stripe LIVE) sau comandă directă.
 */
import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Search, MapPin, Eye, ArrowRight, ShoppingBasket, BadgePercent,
  CreditCard, HandCoins, Loader2, Store, PackageOpen,
} from 'lucide-react';
import PiataHeader from '../components/PiataHeader';
import SiteFooter from '../components/SiteFooter';
import useSEO from '../hooks/useSEO';
import api from '../lib/api';
import { PIATA_CATEGORIES, catMeta, imgUrl, fmtRON } from '../lib/piata';

const HOW_IT_WORKS = [
  { icon: Store, step: '01', title: 'Publici gratuit', desc: 'Îți creezi contul, adaugi produsul cu poze, preț și stoc. Listarea nu costă nimic — niciodată.' },
  { icon: ShoppingBasket, step: '02', title: 'Primești comenzi', desc: 'Cumpărătorii plătesc cu cardul direct pe platformă (Stripe) sau trimit comandă directă cu plata la livrare.' },
  { icon: HandCoins, step: '03', title: 'Încasezi 99%', desc: 'EPD reține doar 1% comision per tranzacție. Restul de 99% este al tău, vizibil transparent în contul de vânzător.' },
];

export default function PiataProducatori() {
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('');
  const [q, setQ] = useState('');

  useSEO({
    title: 'Piața micilor producători · Listare gratuită · Comision 1% · Energy Project Design',
    description: 'Marketplace românesc pentru mici producători: legume, lactate, miere, vinuri, artizanat. Listare 100% gratuită, comision doar 1% per tranzacție, plată cu cardul (Stripe) sau comandă directă la producător.',
    canonical: 'https://www.energyprojectdesign.com/piata',
    keywords: 'piata micilor producatori, marketplace producatori locali, produse de ferma online, listare gratuita, comision 1%, vinde produse locale romania',
    breadcrumbs: [{ name: 'Acasă', url: '/' }, { name: 'Piața producătorilor', url: '/piata' }],
  });

  const load = useCallback(async (cat, query) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (cat) params.append('category', cat);
      if (query) params.append('q', query);
      const { data } = await api.get(`/piata/products?${params.toString()}`);
      setProducts(data.items || []);
    } catch (e) {
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load('', '');
    api.get('/piata/stats').then(({ data }) => setStats(data)).catch(() => {});
  }, [load]);

  const selectCategory = (id) => {
    const next = category === id ? '' : id;
    setCategory(next);
    load(next, q);
  };

  return (
    <div className="min-h-screen bg-white text-zinc-900" data-testid="page-piata">
      <PiataHeader active="piata" />

      {/* HERO compact */}
      <section className="relative pt-24 pb-8 lg:pt-28 lg:pb-10 bg-zinc-950 overflow-hidden">
        <div className="absolute inset-0 opacity-40 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(124,58,237,0.20) 0%, transparent 55%), radial-gradient(circle at 85% 75%, rgba(76,29,149,0.15) 0%, transparent 50%)' }} />
        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative">
          <div className="inline-flex items-center gap-2 px-2.5 py-1 border border-emerald-500/40 rounded-full text-[10px] uppercase tracking-[0.25em] text-emerald-300 mb-4 backdrop-blur-sm bg-emerald-500/5">
            <span className="w-1 h-1 rounded-full bg-emerald-400 animate-pulse" />
            <span>// listare 100% gratuită · comision doar 1% · producători locali</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-[-0.04em] leading-[1.02] mb-4 text-white font-display">
            Piața micilor producători.<br />
            <span className="italic text-violet-400 font-normal">Direct de la sursă, fără intermediari.</span>
          </h1>
          <p className="text-base lg:text-lg text-zinc-300 leading-relaxed mb-6 max-w-2xl">
            Publici gratuit, vinzi cu plata cu cardul sau la livrare, iar EPD reține doar
            <strong className="text-white"> 1% comision</strong> per tranzacție. 99% rămâne la producător.
          </p>
          <div className="flex items-center gap-3 flex-wrap">
            <Link to="/piata/vinde" className="inline-flex items-center gap-2 bg-white text-zinc-950 hover:bg-violet-50 font-semibold px-5 py-2.5 rounded-md text-sm transition-all hover:-translate-y-0.5 shadow" data-testid="piata-hero-sell-cta">
              Vinde produsele tale gratuit <ArrowRight className="w-4 h-4" />
            </Link>
            <div className="flex items-center gap-2 text-xs text-zinc-400">
              <span className="inline-flex items-center gap-1.5 border border-white/15 rounded-md px-2.5 py-1.5"><BadgePercent className="w-3.5 h-3.5 text-emerald-400" /> Comision 1%</span>
              <span className="inline-flex items-center gap-1.5 border border-white/15 rounded-md px-2.5 py-1.5"><CreditCard className="w-3.5 h-3.5 text-violet-400" /> Plată card sau directă</span>
              {stats && <span className="inline-flex items-center gap-1.5 border border-white/15 rounded-md px-2.5 py-1.5" data-testid="piata-stats"><Store className="w-3.5 h-3.5 text-zinc-400" /> {stats.products} produse · {stats.producers} producători</span>}
            </div>
          </div>
        </div>
      </section>

      {/* FILTRE + CATALOG */}
      <section className="py-10 lg:py-12 bg-white border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <div className="relative flex-1 min-w-[220px] max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && load(category, q)}
                placeholder="Caută brânză, miere, țuică, coșuri împletite..."
                className="w-full pl-9 pr-3 py-2 border border-zinc-300 rounded-md text-sm focus:border-zinc-950 outline-none transition-colors"
                data-testid="piata-search-input"
              />
            </div>
            <button onClick={() => load(category, q)} className="epd-btn text-sm py-2" data-testid="piata-search-btn">Caută</button>
          </div>

          <div className="flex gap-2 flex-wrap mb-8">
            {PIATA_CATEGORIES.map((c) => {
              const Icon = c.icon;
              const activeCat = category === c.id;
              return (
                <button
                  key={c.id}
                  onClick={() => selectCategory(c.id)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${activeCat ? 'bg-zinc-950 text-white border-zinc-950' : 'bg-white text-zinc-700 border-zinc-300 hover:border-zinc-950'}`}
                  data-testid={`piata-cat-${c.id}`}
                >
                  <Icon className="w-3.5 h-3.5" strokeWidth={1.5} /> {c.label}
                </button>
              );
            })}
          </div>

          {loading && (
            <div className="text-center py-16 text-zinc-400" data-testid="piata-loading">
              <Loader2 className="w-6 h-6 animate-spin mx-auto" />
            </div>
          )}

          {!loading && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="piata-products-grid">
              {products.map((p) => {
                const meta = catMeta(p.category);
                const Icon = meta.icon;
                return (
                  <Link
                    key={p.product_id}
                    to={`/piata/produs/${p.product_id}`}
                    className="group border border-zinc-200 hover:border-zinc-950 rounded-md overflow-hidden transition-all hover:shadow-lg bg-white flex flex-col"
                    data-testid={`piata-product-${p.product_id}`}
                  >
                    <div className="h-44 bg-zinc-100 overflow-hidden relative">
                      {p.images?.[0] ? (
                        <img src={imgUrl(p.images[0])} alt={p.title} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-zinc-50">
                          <Icon className="w-10 h-10 text-zinc-300" strokeWidth={1.2} />
                        </div>
                      )}
                      {p.stock <= 0 && (
                        <div className="absolute inset-0 bg-zinc-950/60 flex items-center justify-center">
                          <span className="text-white text-xs font-bold uppercase tracking-widest">Stoc epuizat</span>
                        </div>
                      )}
                    </div>
                    <div className="p-4 flex flex-col flex-1">
                      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] text-violet-700 font-bold mb-1.5">
                        <Icon className="w-3 h-3" strokeWidth={1.5} /> {meta.label}
                      </div>
                      <h3 className="text-sm font-bold text-zinc-950 leading-tight mb-1 line-clamp-2">{p.title}</h3>
                      <p className="text-[11px] text-zinc-500 line-clamp-2 mb-3">{p.description}</p>
                      <div className="mt-auto flex items-end justify-between">
                        <div>
                          <div className="text-lg font-bold tabular-nums text-zinc-950 font-display tracking-tight">{fmtRON(p.price_ron)}</div>
                          <div className="text-[10px] text-zinc-500">/ {p.unit}</div>
                        </div>
                        <div className="text-right text-[10px] text-zinc-500">
                          <div className="font-semibold text-zinc-700">{p.seller_name}</div>
                          {p.location && <div className="inline-flex items-center gap-0.5"><MapPin className="w-3 h-3" />{p.location}</div>}
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}

          {!loading && products.length === 0 && (
            <div className="text-center py-16 text-zinc-400 border border-dashed border-zinc-300 rounded-md" data-testid="piata-empty">
              <PackageOpen className="w-10 h-10 mx-auto mb-3 opacity-60" strokeWidth={1.2} />
              <div className="text-sm font-semibold text-zinc-600 mb-1">Niciun produs găsit</div>
              <div className="text-xs mb-4">Fii primul producător care publică — listarea este 100% gratuită.</div>
              <Link to="/piata/vinde" className="epd-btn text-sm py-2 inline-flex" data-testid="piata-empty-sell-cta">Publică primul produs</Link>
            </div>
          )}
        </div>
      </section>

      {/* CUM FUNCȚIONEAZĂ */}
      <section className="py-12 lg:py-16 bg-zinc-50 border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-[10px] uppercase tracking-[0.32em] text-zinc-500 font-bold mb-3">// 3 pași · zero costuri de listare · comision doar la vânzare</div>
          <h2 className="text-3xl lg:text-4xl font-bold tracking-[-0.03em] leading-[1.02] mb-10 text-zinc-950 font-display max-w-3xl">
            Cum funcționează<br /><span className="italic text-zinc-400 font-normal">pentru producători.</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-4">
            {HOW_IT_WORKS.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.step} className="border border-zinc-200 bg-white rounded-md p-5 transition-all hover:border-zinc-950 hover:shadow-md" data-testid={`piata-how-${s.step}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 rounded-md bg-zinc-950 text-white flex items-center justify-center">
                      <Icon className="w-4 h-4" strokeWidth={1.5} />
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-bold">Pas {s.step}</div>
                  </div>
                  <h3 className="text-lg font-bold text-zinc-950 tracking-tight mb-2 font-display">{s.title}</h3>
                  <p className="text-xs text-zinc-600 leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-12 lg:py-16 bg-zinc-950 text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <div className="text-[10px] uppercase tracking-[0.32em] text-violet-400 font-bold mb-3">// listare gratuită · comision 1% · plăți securizate stripe</div>
          <h2 className="text-3xl lg:text-5xl font-bold tracking-[-0.03em] leading-[1.02] mb-4 font-display">
            Produsele tale merită <span className="italic text-zinc-400 font-normal">o piață corectă.</span>
          </h2>
          <p className="text-base text-zinc-400 leading-relaxed mb-8 max-w-2xl mx-auto">
            Fără taxe ascunse, fără abonamente obligatorii. Publici în 2 minute, iar 99% din fiecare
            vânzare rămâne la tine.
          </p>
          <Link to="/piata/vinde" className="inline-flex items-center gap-2 bg-white text-zinc-950 hover:bg-violet-50 font-semibold px-6 py-3 rounded-md text-sm transition-all hover:-translate-y-0.5 shadow" data-testid="piata-footer-sell-cta">
            Începe să vinzi gratuit <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
